import { useState, useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { DocumentIcon, CloudArrowUpIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { DocumentUploadData } from '../../../hooks/useRegistrationWizard';

interface DocumentUploadStepProps {
  form: UseFormReturn<DocumentUploadData>;
}

interface UploadedFile {
  file: File;
  name: string;
  url?: string;
  preview?: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
};

interface FileUploaderProps {
  label: string;
  description: string;
  file: UploadedFile | null;
  onUpload: (file: File) => void;
  onRemove: () => void;
  error?: string;
  required?: boolean;
}

function FileUploader({ label, description, file, onUpload, onRemove, error, required }: FileUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const selectedFile = acceptedFiles[0];

      // Validate file size
      if (selectedFile.size > MAX_FILE_SIZE) {
        alert('File size must be less than 10MB');
        return;
      }

      setUploading(true);
      setUploadProgress(0);

      // Simulate upload progress
      const interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(interval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      try {
        // TODO: Replace with actual S3 presigned URL upload
        await new Promise((resolve) => setTimeout(resolve, 2000));

        setUploadProgress(100);
        onUpload(selectedFile);
      } catch (error) {
        console.error('Upload failed:', error);
        alert('Upload failed. Please try again.');
      } finally {
        clearInterval(interval);
        setTimeout(() => {
          setUploading(false);
          setUploadProgress(0);
        }, 500);
      }
    },
    [onUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxFiles: 1,
    multiple: false,
  });

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <p className="text-sm text-gray-500">{description}</p>

      {!file ? (
        <div
          {...getRootProps()}
          className={clsx(
            'border-2 border-dashed rounded-lg p-6 transition-colors cursor-pointer',
            isDragActive
              ? 'border-primary-500 bg-primary-50'
              : error
              ? 'border-red-300 bg-red-50'
              : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
          )}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center text-center">
            <CloudArrowUpIcon
              className={clsx(
                'h-12 w-12 mb-3',
                isDragActive ? 'text-primary-500' : 'text-gray-400'
              )}
            />
            <p className="text-sm font-medium text-gray-900">
              {isDragActive ? 'Drop file here' : 'Click to upload or drag and drop'}
            </p>
            <p className="text-xs text-gray-500 mt-1">PDF, JPG, or PNG (max 10MB)</p>
          </div>
        </div>
      ) : (
        <div className="border border-gray-300 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1 min-w-0">
              <DocumentIcon className="h-10 w-10 text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {(file.file.size / 1024 / 1024).toFixed(2)} MB
                </p>
                {file.preview && (
                  <div className="mt-2">
                    <img
                      src={file.preview}
                      alt="Preview"
                      className="h-20 w-auto rounded border border-gray-200"
                    />
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onRemove}
              className="ml-3 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          {!uploading && (
            <div className="flex items-center mt-2 text-green-600">
              <CheckCircleIcon className="h-5 w-5 mr-1.5" />
              <span className="text-sm font-medium">Uploaded successfully</span>
            </div>
          )}
        </div>
      )}

      {uploading && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Uploading...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-primary-600 h-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

export function DocumentUploadStep({ form }: DocumentUploadStepProps) {
  const {
    watch,
    setValue,
    formState: { errors },
  } = form;

  const governmentId = watch('governmentId');
  const professionalLicense = watch('professionalLicense');
  const insuranceCertificate = watch('insuranceCertificate');

  const handleUpload = (field: keyof DocumentUploadData, file: File) => {
    // Create preview for images
    let preview: string | undefined;
    if (file.type.startsWith('image/')) {
      preview = URL.createObjectURL(file);
    }

    setValue(field, {
      file,
      name: file.name,
      preview,
    });
  };

  const handleRemove = (field: keyof DocumentUploadData) => {
    const current = watch(field);
    if (current?.preview) {
      URL.revokeObjectURL(current.preview);
    }
    setValue(field, null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Document Upload</h2>
        <p className="mt-2 text-sm text-gray-600">
          Upload the required documents for verification. All files are securely encrypted.
        </p>
      </div>

      <div className="space-y-6">
        <FileUploader
          label="Government-Issued ID"
          description="Driver's license, passport, or state ID"
          file={governmentId}
          onUpload={(file) => handleUpload('governmentId', file)}
          onRemove={() => handleRemove('governmentId')}
          error={errors.governmentId?.message}
          required
        />

        <FileUploader
          label="Professional License"
          description="Copy of your current professional license"
          file={professionalLicense}
          onUpload={(file) => handleUpload('professionalLicense', file)}
          onRemove={() => handleRemove('professionalLicense')}
          error={errors.professionalLicense?.message}
          required
        />

        <FileUploader
          label="Certificate of Insurance (COI)"
          description="Professional liability insurance certificate"
          file={insuranceCertificate}
          onUpload={(file) => handleUpload('insuranceCertificate', file)}
          onRemove={() => handleRemove('insuranceCertificate')}
          error={errors.insuranceCertificate?.message}
          required
        />
      </div>

      <div className="rounded-lg bg-green-50 p-4 border border-green-200">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-green-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">Secure Document Storage</h3>
            <div className="mt-2 text-sm text-green-700">
              <p>
                All uploaded documents are encrypted at rest and in transit using industry-standard
                encryption. Your documents are stored securely and are only accessible to
                authorized verification personnel.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
